import { useMemo } from 'react';

import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useItemsSelectedProposalColor } from '@features/proposals/hooks/use-proposals.hooks';
import {
  attachBaselineCo2BehindProposalBar,
  buildProposalBarEntry,
  getBaselineCo2ValuePerGfaForItemIds,
} from '@features/proposals/ElementListRow/proposal-bar.helpers';
import type { ProposalBar } from '@features/proposals/ElementListRow/ElementListRow';
import { useProposalResultsRecord } from '@features/proposals/use-proposals.hooks';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { useSelectedVersion } from '@client/store/ui';
import {
  useGetResultsPerGFA,
  useVersionResultRecord,
} from '@features/results/use-results.hooks';
import { sumConversionFactors } from '@shared/helpers/conversion-factors.helpers';

/**
 * Aggregated proposal bars for a list of product items (a product group or
 * category group). Mirrors {@link useElementListRowProposalBars}:
 * Returns a single bar built from the items' results in the selected proposal
 * record (or baseline when none is selected).
 *
 * When a proposal is selected and the row is affected by it (color is not
 * baseline grey), the bar also carries `baselineCo2Value` so the row
 * renders a grey baseline bar behind the proposal bar. Baseline is summed
 * directly from the unresolved version's `resultsRecord`, since the
 * proposal-resolved record otherwise drives both numbers and the grey bar
 * would never extend past the proposal bar.
 */
export const useProductGroupProposalBars = (
  itemIds: readonly string[],
): ProposalBar[] => {
  const getResultsPerGFA = useGetResultsPerGFA();
  const displayCurrency = useDisplayCurrency();

  const proposalColor = useItemsSelectedProposalColor(itemIds);
  // itemIds are already SBEF-scoped at product-group build time, so use flat per-row
  // totals here; a row-level SBEF collapse would zero these bars (DEV-3492).
  const selectedProposalRecord = useProposalResultsRecord(undefined, {
    applySbefFilter: false,
  });
  const selectedProposalId = useSelectedProposalId();
  const selectedVersion = useSelectedVersion();
  const baselineResultRecord = useVersionResultRecord(selectedVersion);

  return useMemo(() => {
    const summed = sumConversionFactors(
      ...itemIds.map((id) => selectedProposalRecord[id] ?? {}),
    );
    const bar = buildProposalBarEntry(
      getResultsPerGFA(summed),
      proposalColor,
      displayCurrency,
    );
    return [
      attachBaselineCo2BehindProposalBar(bar, {
        selectedProposalId,
        proposalColor,
        hasSelectedVersion: selectedVersion != null,
        baselineCo2Value: getBaselineCo2ValuePerGfaForItemIds(
          itemIds,
          baselineResultRecord,
          getResultsPerGFA,
        ),
      }),
    ];
  }, [
    selectedProposalRecord,
    proposalColor,
    displayCurrency,
    itemIds,
    getResultsPerGFA,
    selectedProposalId,
    selectedVersion,
    baselineResultRecord,
  ]);
};
